import Phaser from 'phaser';

import { BlackjackMainScene } from '../../scenes/blackjack-main-scene';
import { Subscription } from 'rxjs';
import { ResizeMetrics } from '../../model/resize-metrics';
// import { DebugContainerVisual } from '../debug/debug-container-visual';
import { BlackjackEvents, GameEvent } from 'game-logic';
import { delay } from 'src/games/common/helper';
import { CardView } from '../cards/card-view';
import { CardDeckViewModel } from './card-deck-view-model';

export class CardDeckView extends Phaser.GameObjects.Container {
  private currentResizeMetrics?: ResizeMetrics;
  private subscriptionGroup: Subscription;

  private cardViews: CardView[] = [];

  constructor(
    scene: BlackjackMainScene,
    x: number,
    y: number,
    private viewModel: CardDeckViewModel,
  ) {
    super(scene, x, y);

    // new DebugContainerVisual(scene, this);

    this.subscriptionGroup = new Subscription();

    this.setName(CardDeckView.name);
    scene.add.existing(this);

    this.subscriptionGroup.add(
      this.viewModel.onResetGameScene$.subscribe(() => {
        for (const cardView of this.cardViews) {
          cardView.destroy();
        }
        this.cardViews = [];
      }),
    );
    this.subscriptionGroup.add(
      this.viewModel.cardViewModelChanges$.subscribe((change) => {
        const scaleFactor = this.currentResizeMetrics?.scalingMetrics.scaleFactor ?? 1;
        const boardWidth = this.currentResizeMetrics?.layoutConfig.boardWidth ?? 500;

        // Add the new card view
        const cardView = new CardView(scene, 0, 0, change.viewModel);
        if (this.currentResizeMetrics) {
          cardView.onResize(this.currentResizeMetrics);
        }
        this.add(cardView);

        const cardCount = this.cardViews.length;
        this.cardViews.push(cardView);

        const cardIndex = change.viewModel.cardIndex / 52;
        const randY = -1 * (cardIndex * 0.5 - 0.25);
        const initialX = boardWidth * 0.25 * scaleFactor;
        const initialY = boardWidth * randY * scaleFactor;

        cardView.setPosition(initialX, initialY);

        this.scene.tweens.add({
          targets: [cardView],
          x: 0,
          y: 0,
          duration: 350,
          ease: Phaser.Math.Easing.Linear,
          onComplete: () => {
            if (cardCount > 0) {
              change.viewModel.enableShadow(false);
              cardView.setVisible(false);
            }
          },
        });
      }),
    );
    this.subscriptionGroup.add(
      viewModel.logicEventQueue$.subscribe((eventQueue) => {
        eventQueue?.subscribe(this.handleLogicEvents.bind(this));
      }),
    );
  }

  override destroy(fromScene?: boolean | undefined): void {
    this.subscriptionGroup.unsubscribe();

    super.destroy(fromScene);
  }

  public onResize(resizeMetrics: ResizeMetrics) {
    this.cardViews.forEach((cardView) => cardView.onResize(resizeMetrics));

    this.currentResizeMetrics = resizeMetrics;
    this.refreshLayout();
  }

  private refreshLayout(): void {
    if (!this.currentResizeMetrics) {
      return;
    }
    const layoutConfig = this.currentResizeMetrics.layoutConfig;
    const scalingMetrics = this.currentResizeMetrics.scalingMetrics;

    const boxScale = 0.8;

    const controlsWidth = layoutConfig.bettingBoxWidth * boxScale;
    const controlsHeight = layoutConfig.bettingBoxHeight * boxScale;

    const xPos = layoutConfig.boardWidth - controlsWidth * 0.5 - layoutConfig.paddingSm;
    const yPos = layoutConfig.boardHeight * 0.15;

    this.setSize(
      controlsWidth * scalingMetrics.scaleFactor,
      controlsHeight * scalingMetrics.scaleFactor,
    );
    this.setPosition(
      xPos * scalingMetrics.scaleFactor + scalingMetrics.scaledOffsetX,
      yPos * scalingMetrics.scaleFactor,
    );
  }

  private async handleLogicEvents(event: GameEvent<BlackjackEvents>): Promise<void> {
    // NOTE: We always process game init events
    switch (event.type) {
      case 'onGameInit':
        {
          const board = event.payload.board;
          for (const [index, card] of board.deck.allCards.entries()) {
            // This is only visual, so cut the number of cards to 52
            if (index > 52) {
              continue;
            }
            this.viewModel.addDeckCard(card, index);
            if (!event.instant) {
              await delay(10);
            }
          }
        }
        break;
      default:
        break;
    }
  }
}
